<template>
  <div class="tdp-wrap">
    <!-- ══ KPI ALERT CARDS ══ -->
    <div class="tdp-alert-row">
      <div class="tdp-alert-card tdp-alert-card--red">
        <div class="tdp-alert-icon">
          <v-icon size="22" color="#dc2626">mdi-trending-down</v-icon>
        </div>
        <div class="tdp-alert-body">
          <div class="tdp-alert-val">{{ decliningProvinces.length }}</div>
          <div class="tdp-alert-lbl">จังหวัดนักเรียนลดลง</div>
          <div class="tdp-alert-sub">ต่อเนื่อง 3 ปี 2566–2568</div>
        </div>
      </div>
      <div class="tdp-alert-card tdp-alert-card--orange">
        <div class="tdp-alert-icon">
          <v-icon size="22" color="#ea580c">mdi-alert-outline</v-icon>
        </div>
        <div class="tdp-alert-body">
          <div class="tdp-alert-val">{{ atRiskColleges.length }}</div>
          <div class="tdp-alert-lbl">สถานศึกษาเสี่ยง</div>
          <div class="tdp-alert-sub">
            นักเรียน &lt; {{ riskThreshold }} หรือลด &gt; 15%
          </div>
        </div>
      </div>
      <div class="tdp-alert-card tdp-alert-card--amber">
        <div class="tdp-alert-icon">
          <v-icon size="22" color="#d97706">mdi-minus-circle-outline</v-icon>
        </div>
        <div class="tdp-alert-body">
          <div class="tdp-alert-val">{{ formatNum(totalDecline) }}</div>
          <div class="tdp-alert-lbl">นักเรียนลดลงสุทธิ (คน)</div>
          <div class="tdp-alert-sub">2566 → 2568</div>
        </div>
      </div>
      <div class="tdp-alert-card tdp-alert-card--blue">
        <div class="tdp-alert-icon">
          <v-icon size="22" color="#2563eb">mdi-percent</v-icon>
        </div>
        <div class="tdp-alert-body">
          <div class="tdp-alert-val">{{ overallChangePct }}%</div>
          <div class="tdp-alert-lbl">อัตราเปลี่ยนแปลงรวม</div>
          <div class="tdp-alert-sub">เทียบ 2566 กับ 2568</div>
        </div>
      </div>
    </div>

    <!-- ══ ROW: TREND CHART + DECLINING PROVINCES ══ -->
    <div class="tdp-main-row">
      <!-- Trend line chart (ApexCharts) -->
      <div class="tdp-panel" style="flex:2.2">
        <div class="tdp-panel-head">
          <v-icon size="15" color="#2563eb" class="mr-2">mdi-chart-line</v-icon>
          <span>แนวโน้มนักเรียน 3 ปี (2566–2568)</span>
          <v-spacer />
          <div class="tdp-tab-grp">
            <span
              v-for="t in trendViewOptions"
              :key="t.key"
              :class="['tdp-tab', { active: trendView === t.key }]"
              @click="trendView = t.key"
              >{{ t.label }}</span
            >
          </div>
        </div>
        <apexchart
          v-if="trendSeries.length"
          type="area"
          height="250"
          :options="trendOpts"
          :series="trendSeries"
        />
        <div v-else class="tdp-no-data" style="height:250px">ไม่มีข้อมูล</div>
        <div class="tdp-insight-bar" v-if="trendInsightText">
          <v-icon
            size="13"
            :color="overallChangePct < 0 ? '#dc2626' : '#16a34a'"
            class="mr-1"
          >
            {{ overallChangePct < 0 ? "mdi-trending-down" : "mdi-trending-up" }}
          </v-icon>
          <span>{{ trendInsightText }}</span>
        </div>
      </div>

      <!-- Top declining provinces -->
      <div class="tdp-panel" style="flex:1.4">
        <div class="tdp-panel-head">
          <v-icon size="15" color="#dc2626" class="mr-2"
            >mdi-map-marker-alert</v-icon
          >
          <span>จังหวัดที่ลดลงมากที่สุด</span>
          <v-chip x-small outlined color="#dc2626" class="ml-1"
            >Top {{ topN }}</v-chip
          >
        </div>
        <div class="tdp-dec-list">
          <div
            v-for="(p, i) in topDecliningProvinces"
            :key="p.province"
            class="tdp-dec-row"
          >
            <div class="tdp-dec-rank" :class="i < 3 ? 'tdp-dec-rank--hot' : ''">
              {{ i + 1 }}
            </div>
            <div class="tdp-dec-info">
              <div class="tdp-dec-name">{{ p.province }}</div>
              <div class="tdp-dec-region">{{ p.region }}</div>
            </div>
            <div class="tdp-dec-nums">
              <div class="tdp-dec-change tdp-dec-change--neg">
                ▼ {{ formatNum(Math.abs(p.change)) }}
              </div>
              <div class="tdp-dec-pct tdp-dec-pct--neg">{{ p.changePct }}%</div>
            </div>
            <div class="tdp-dec-sparkbar">
              <div class="tdp-dec-bar-bg">
                <div
                  class="tdp-dec-bar-fill"
                  :style="
                    `width:${p.barPct}%;background:${
                      i < 3 ? '#dc2626' : '#f87171'
                    }`
                  "
                ></div>
              </div>
            </div>
          </div>
          <div
            v-if="!topDecliningProvinces.length"
            class="tdp-no-data"
            style="height:120px"
          >
            ไม่มีข้อมูล
          </div>
        </div>
      </div>
    </div>

    <!-- ══ ROW: AT-RISK COLLEGES ══ -->
    <div class="tdp-panel">
      <div class="tdp-panel-head">
        <v-icon size="15" color="#ea580c" class="mr-2"
          >mdi-alert-circle-outline</v-icon
        >
        <span>สถานศึกษาเสี่ยง — นักเรียนน้อยกว่าเกณฑ์หรือลดลงต่อเนื่อง</span>
        <v-chip x-small outlined color="#ea580c" class="ml-1"
          >{{ atRiskColleges.length }} แห่ง</v-chip
        >
        <v-spacer />
        <div class="tdp-tab-grp">
          <span
            v-for="t in riskSortOptions"
            :key="t.key"
            :class="['tdp-tab', { active: riskSort === t.key }]"
            @click="riskSort = t.key"
            >{{ t.label }}</span
          >
        </div>
        <v-text-field
          v-model="riskSearch"
          dense
          outlined
          hide-details
          clearable
          placeholder="ค้นหา..."
          prepend-inner-icon="mdi-magnify"
          class="tdp-search ml-2"
        />
      </div>
      <div class="tdp-risk-tbl-wrap">
        <table class="tdp-risk-tbl">
          <thead>
            <tr>
              <th>สถานศึกษา</th>
              <th>จังหวัด / ภาค</th>
              <th class="ta-r">2566</th>
              <th class="ta-r">2567</th>
              <th class="ta-r">2568</th>
              <th class="ta-r">เปลี่ยนแปลง</th>
              <th class="ta-r">% 3 ปี</th>
              <th style="width:88px">แนวโน้ม</th>
              <th>ระดับเสี่ยง</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in sortedRiskColleges"
              :key="c.code"
              class="tdp-risk-row"
            >
              <td class="tdp-risk-name">{{ c.name }}</td>
              <td>
                <div class="tdp-risk-prov">{{ c.province }}</div>
                <div class="tdp-risk-region">{{ c.region }}</div>
              </td>
              <td class="ta-r mono-sm">{{ formatNum(c.y2566) }}</td>
              <td class="ta-r mono-sm">{{ formatNum(c.y2567) }}</td>
              <td class="ta-r mono-sm fw6">{{ formatNum(c.y2568) }}</td>
              <td class="ta-r">
                <span :class="c.change < 0 ? 'chg-neg' : 'chg-pos'">
                  {{ c.change < 0 ? "▼" : "▲"
                  }}{{ formatNum(Math.abs(c.change)) }}
                </span>
              </td>
              <td class="ta-r">
                <span :class="c.changePct < 0 ? 'chg-neg' : 'chg-pos'"
                  >{{ c.changePct }}%</span
                >
              </td>
              <td>
                <!-- SVG sparkline — ไม่พึ่ง library ใด ๆ -->
                <svg width="80" height="28" :aria-label="`แนวโน้ม ${c.name}`">
                  <polyline
                    :points="sparkPoints(c)"
                    fill="none"
                    :stroke="c.change < 0 ? '#dc2626' : '#16a34a'"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                  <circle
                    v-for="(pt, pi) in sparkDots(c)"
                    :key="pi"
                    :cx="pt.x"
                    :cy="pt.y"
                    r="2.5"
                    :fill="c.change < 0 ? '#dc2626' : '#16a34a'"
                  />
                </svg>
              </td>
              <td>
                <span class="risk-badge" :class="`risk-badge--${c.riskLevel}`">
                  {{ riskLabel(c.riskLevel) }}
                </span>
              </td>
            </tr>
            <tr v-if="!sortedRiskColleges.length">
              <td colspan="9" class="tdp-no-data ta-c" style="padding:18px">
                ไม่พบสถานศึกษาเสี่ยง
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="tdp-load-more"
        v-if="filteredRiskCount > riskPage * 25"
        @click="riskPage++"
      >
        โหลดเพิ่มเติม ({{ filteredRiskCount - riskPage * 25 }} รายการ)
      </div>
    </div>

    <!-- ══ ROW: REGION COMPARISON (ApexCharts) ══ -->
    <div class="tdp-panel">
      <div class="tdp-panel-head">
        <v-icon size="15" color="#7c3aed" class="mr-2"
          >mdi-chart-bar-stacked</v-icon
        >
        <span>เปรียบเทียบนักเรียนรายภาค 3 ปี (2566–2568)</span>
        <v-spacer />
        <div class="tdp-tab-grp">
          <span
            v-for="t in regionChartTypes"
            :key="t.key"
            :class="['tdp-tab', { active: regionChartType === t.key }]"
            @click="regionChartType = t.key"
            >{{ t.label }}</span
          >
        </div>
      </div>
      <apexchart
        v-if="regionSeries.length"
        :type="regionChartType === 'change' ? 'bar' : 'bar'"
        height="230"
        :options="regionOpts"
        :series="regionSeries"
      />
      <div v-else class="tdp-no-data" style="height:230px">ไม่มีข้อมูล</div>
    </div>
  </div>
</template>

<script>
/**
 * TrendDeclinePanel.vue  — ใช้ ApexCharts + SVG sparkline (ไม่พึ่ง Chart.js)
 *
 * Props:
 *   data2566, data2567, data2568 : Array  — student rows รายปี
 *   riskThreshold                : Number — นักเรียน < N = เสี่ยง (default 200)
 */

const REGION_PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#db2777"
];

function rowTotal(r) {
  return (
    (+r.rate_work_field_study_lavel_1 || 0) +
    (+r.rate_work_field_study_lavel_2 || 0) +
    (+r.rate_work_field_study_lavel_3 || 0)
  );
}

function toCollegeMap(data) {
  const m = {};
  data.forEach(r => {
    const k = r.rate_work_college_code;
    if (!k) return;
    if (!m[k])
      m[k] = {
        code: k,
        name: r.college_name || k,
        province: r.province_name || "",
        region: r.region_name || "",
        total: 0
      };
    m[k].total += rowTotal(r);
  });
  return m;
}

function toProvinceMap(data) {
  const m = {};
  data.forEach(r => {
    const k = r.province_name || "ไม่ระบุ";
    if (!m[k]) m[k] = { province: k, region: r.region_name || "", total: 0 };
    m[k].total += rowTotal(r);
  });
  return m;
}

function toRegionMap(data) {
  const m = {};
  data.forEach(r => {
    const k = r.region_name || "ไม่ระบุ";
    if (!m[k]) m[k] = 0;
    m[k] += rowTotal(r);
  });
  return m;
}

function sparkPts(vals, w, h, pad) {
  const min = Math.min(...vals),
    max = Math.max(...vals);
  const range = max - min || 1;
  return vals.map((v, i) => ({
    x: +(pad + (i / (vals.length - 1)) * (w - pad * 2)).toFixed(1),
    y: +(pad + (1 - (v - min) / range) * (h - pad * 2)).toFixed(1)
  }));
}

const APEX_BASE = {
  chart: {
    background: "transparent",
    foreColor: "#374151",
    toolbar: { show: false },
    animations: { enabled: false }
  },
  grid: { borderColor: "#e5e7eb", strokeDashArray: 3 },
  legend: { show: false },
  fontFamily: "Sarabun, sans-serif"
};

export default {
  name: "TrendDeclinePanel",

  props: {
    data2566: { type: Array, default: () => [] },
    data2567: { type: Array, default: () => [] },
    data2568: { type: Array, default: () => [] },
    riskThreshold: { type: Number, default: 200 }
  },

  data() {
    return {
      trendView: "total",
      trendViewOptions: [
        { key: "total", label: "รวม" },
        { key: "level", label: "แยกระดับ" },
        { key: "region", label: "แยกภาค" }
      ],
      regionChartType: "grouped",
      regionChartTypes: [
        { key: "grouped", label: "เปรียบเทียบ" },
        { key: "change", label: "% เปลี่ยนแปลง" }
      ],
      topN: 10,
      riskSort: "change",
      riskSortOptions: [
        { key: "change", label: "ลดลงมากสุด" },
        { key: "pct", label: "% ลดลงสุด" },
        { key: "absolute", label: "น้อยสุด (2568)" }
      ],
      riskSearch: "",
      riskPage: 1
    };
  },

  computed: {
    /* ── aggregated maps ── */
    college2566() {
      return toCollegeMap(this.data2566);
    },
    college2567() {
      return toCollegeMap(this.data2567);
    },
    college2568() {
      return toCollegeMap(this.data2568);
    },
    province2566() {
      return toProvinceMap(this.data2566);
    },
    province2567() {
      return toProvinceMap(this.data2567);
    },
    province2568() {
      return toProvinceMap(this.data2568);
    },
    region2566() {
      return toRegionMap(this.data2566);
    },
    region2567() {
      return toRegionMap(this.data2567);
    },
    region2568() {
      return toRegionMap(this.data2568);
    },

    totalByYear() {
      const sum = d => d.reduce((s, r) => s + rowTotal(r), 0);
      return {
        y2566: sum(this.data2566),
        y2567: sum(this.data2567),
        y2568: sum(this.data2568)
      };
    },

    totalDecline() {
      return this.totalByYear.y2568 - this.totalByYear.y2566;
    },

    overallChangePct() {
      const { y2566, y2568 } = this.totalByYear;
      if (!y2566) return 0;
      return +(((y2568 - y2566) / y2566) * 100).toFixed(1);
    },

    trendInsightText() {
      const { y2566, y2567, y2568 } = this.totalByYear;
      if (!y2566) return "";
      const chg = y2568 - y2566,
        pct = this.overallChangePct;
      return `ภาพรวม 3 ปี: ${chg < 0 ? "ลดลง" : "เพิ่มขึ้น"} ${this.formatNum(
        Math.abs(chg)
      )} คน (${Math.abs(pct)}%) | 2566: ${this.formatNum(
        y2566
      )} → 2567: ${this.formatNum(y2567)} → 2568: ${this.formatNum(y2568)}`;
    },

    /* ── APEX trend series ── */
    trendSeries() {
      const { y2566, y2567, y2568 } = this.totalByYear;
      if (!y2566 && !y2567 && !y2568) return [];

      if (this.trendView === "total") {
        return [{ name: "นักเรียนรวม", data: [y2566, y2567, y2568] }];
      }
      if (this.trendView === "level") {
        const lvl = (data, cls) =>
          data
            .filter(r => r.rate_work_college_id_class == cls)
            .reduce((s, r) => s + rowTotal(r), 0);
        return [
          {
            name: "ปวช.",
            data: [
              lvl(this.data2566, 20),
              lvl(this.data2567, 20),
              lvl(this.data2568, 20)
            ]
          },
          {
            name: "ปวส.",
            data: [
              lvl(this.data2566, 30),
              lvl(this.data2567, 30),
              lvl(this.data2568, 30)
            ]
          }
        ];
      }
      // region
      const regions = [
        ...new Set([
          ...Object.keys(this.region2566),
          ...Object.keys(this.region2568)
        ])
      ]
        .filter(r => r !== "ไม่ระบุ")
        .slice(0, 7);
      return regions.map(r => ({
        name: r,
        data: [
          this.region2566[r] || 0,
          this.region2567[r] || 0,
          this.region2568[r] || 0
        ]
      }));
    },

    trendOpts() {
      const colors =
        this.trendView === "total"
          ? ["#2563eb"]
          : this.trendView === "level"
          ? ["#2563eb", "#7c3aed"]
          : REGION_PALETTE;

      return {
        ...APEX_BASE,
        chart: { ...APEX_BASE.chart, type: "area" },
        colors,
        stroke: { curve: "smooth", width: this.trendView === "region" ? 2 : 3 },
        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: this.trendView === "region" ? 0 : 0.18,
            opacityTo: 0.01
          }
        },
        markers: { size: 5, strokeWidth: 0 },
        xaxis: {
          categories: ["2566", "2567", "2568"],
          labels: {
            style: { fontFamily: "Sarabun,sans-serif", fontSize: "12px" }
          }
        },
        yaxis: {
          labels: {
            style: { fontFamily: "Sarabun,sans-serif", fontSize: "11px" },
            formatter: v => (v ? (+v).toLocaleString("th-TH") : "0")
          }
        },
        tooltip: {
          y: {
            formatter: v => (v ? (+v).toLocaleString("th-TH") + " คน" : "-")
          }
        },
        legend: {
          show: this.trendView === "region",
          fontFamily: "Sarabun,sans-serif",
          fontSize: "11px"
        },
        dataLabels: { enabled: false }
      };
    },

    /* ── declining provinces ── */
    decliningProvinces() {
      const all = new Set([
        ...Object.keys(this.province2566),
        ...Object.keys(this.province2568)
      ]);
      return [...all]
        .map(p => {
          const s = this.province2566[p]?.total || 0;
          const e = this.province2568[p]?.total || 0;
          return {
            province: p,
            region:
              this.province2568[p]?.region ||
              this.province2566[p]?.region ||
              "",
            start: s,
            end: e,
            change: e - s
          };
        })
        .filter(p => p.change < 0);
    },

    topDecliningProvinces() {
      const sorted = [...this.decliningProvinces]
        .sort((a, b) => a.change - b.change)
        .slice(0, this.topN);
      const maxAbs = sorted.length ? Math.abs(sorted[0].change) : 1;
      return sorted.map(p => ({
        ...p,
        changePct: p.start ? +((p.change / p.start) * 100).toFixed(1) : 0,
        barPct: Math.round((Math.abs(p.change) / maxAbs) * 100)
      }));
    },

    /* ── at-risk colleges ── */
    atRiskColleges() {
      const all = new Set([
        ...Object.keys(this.college2566),
        ...Object.keys(this.college2567),
        ...Object.keys(this.college2568)
      ]);
      const result = [];
      all.forEach(code => {
        const c66 = this.college2566[code],
          c67 = this.college2567[code],
          c68 = this.college2568[code];
        const y2566 = c66?.total || 0,
          y2567 = c67?.total || 0,
          y2568 = c68?.total || 0;
        const change = y2568 - y2566;
        const changePct = y2566 ? +((change / y2566) * 100).toFixed(1) : 0;
        const info = c68 || c67 || c66;
        const isTooSmall = y2568 > 0 && y2568 < this.riskThreshold;
        const isBigDrop = changePct < -15 && y2566 > 0;
        const isConsecutive = y2567 < y2566 && y2568 < y2567;
        if (isTooSmall || isBigDrop || isConsecutive) {
          const cnt = [isTooSmall, isBigDrop, isConsecutive].filter(Boolean)
            .length;
          let riskLevel =
            cnt >= 3
              ? "critical"
              : cnt === 2
              ? "high"
              : isBigDrop && changePct < -25
              ? "high"
              : "low";
          result.push({
            code,
            name: info?.name || code,
            province: info?.province || "",
            region: info?.region || "",
            y2566,
            y2567,
            y2568,
            change,
            changePct,
            riskLevel
          });
        }
      });
      return result;
    },

    filteredRiskCount() {
      if (!this.riskSearch) return this.atRiskColleges.length;
      const q = this.riskSearch.toLowerCase();
      return this.atRiskColleges.filter(
        c =>
          c.name.toLowerCase().includes(q) ||
          c.province.toLowerCase().includes(q)
      ).length;
    },

    sortedRiskColleges() {
      let arr = [...this.atRiskColleges];
      if (this.riskSearch) {
        const q = this.riskSearch.toLowerCase();
        arr = arr.filter(
          c =>
            c.name.toLowerCase().includes(q) ||
            c.province.toLowerCase().includes(q)
        );
      }
      if (this.riskSort === "change") arr.sort((a, b) => a.change - b.change);
      else if (this.riskSort === "pct")
        arr.sort((a, b) => a.changePct - b.changePct);
      else arr.sort((a, b) => a.y2568 - b.y2568);
      return arr.slice(0, this.riskPage * 25);
    },

    /* ── APEX region series ── */
    regionSeries() {
      const regions = [
        ...new Set([
          ...Object.keys(this.region2566),
          ...Object.keys(this.region2568)
        ])
      ].filter(r => r && r !== "ไม่ระบุ");
      if (!regions.length) return [];

      if (this.regionChartType === "grouped") {
        return [
          { name: "2566", data: regions.map(r => this.region2566[r] || 0) },
          { name: "2567", data: regions.map(r => this.region2567[r] || 0) },
          { name: "2568", data: regions.map(r => this.region2568[r] || 0) }
        ];
      }
      // % change
      return [
        {
          name: "% เปลี่ยนแปลง",
          data: regions.map(r => {
            const s = this.region2566[r] || 0,
              e = this.region2568[r] || 0;
            return s ? +(((e - s) / s) * 100).toFixed(1) : 0;
          })
        }
      ];
    },

    regionOpts() {
      const regions = [
        ...new Set([
          ...Object.keys(this.region2566),
          ...Object.keys(this.region2568)
        ])
      ].filter(r => r && r !== "ไม่ระบุ");

      const isChange = this.regionChartType === "change";
      const colors = isChange
        ? regions.map(r => {
            const s = this.region2566[r] || 0,
              e = this.region2568[r] || 0;
            return e - s < 0 ? "#fca5a5" : "#86efac";
          })
        : ["#93c5fd", "#a5b4fc", "#2563eb"];

      return {
        ...APEX_BASE,
        chart: { ...APEX_BASE.chart, type: "bar" },
        colors,
        plotOptions: {
          bar: {
            borderRadius: 3,
            columnWidth: isChange ? "55%" : "75%",
            distributed: isChange
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: regions,
          labels: {
            style: { fontFamily: "Sarabun,sans-serif", fontSize: "11px" },
            rotate: -25
          }
        },
        yaxis: {
          labels: {
            style: { fontFamily: "Sarabun,sans-serif", fontSize: "11px" },
            formatter: v =>
              isChange ? v.toFixed(1) + "%" : (+v).toLocaleString("th-TH")
          }
        },
        tooltip: {
          y: {
            formatter: v =>
              isChange
                ? v.toFixed(1) + "%"
                : (+v).toLocaleString("th-TH") + " คน"
          }
        },
        legend: {
          show: !isChange,
          fontFamily: "Sarabun,sans-serif",
          fontSize: "11px"
        }
      };
    }
  },

  watch: {
    // รอให้ทั้ง 3 props มาถึง แล้วค่อย recompute (computed จัดการเอง ไม่ต้อง build chart ด้วยมือ)
    riskSearch() {
      this.riskPage = 1;
    }
  },

  methods: {
    formatNum(n) {
      if (n === null || n === undefined) return "-";
      return (+n).toLocaleString("th-TH");
    },
    riskLabel(lvl) {
      return { critical: "วิกฤต", high: "สูง", low: "ปานกลาง" }[lvl] || lvl;
    },

    /* SVG sparkline helpers */
    sparkPoints(c) {
      const pts = sparkPts([c.y2566, c.y2567, c.y2568], 80, 28, 4);
      return pts.map(p => `${p.x},${p.y}`).join(" ");
    },
    sparkDots(c) {
      return sparkPts([c.y2566, c.y2567, c.y2568], 80, 28, 4);
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap");

.tdp-wrap {
  font-family: "Sarabun", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── ALERT CARDS ── */
.tdp-alert-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.tdp-alert-card {
  flex: 1;
  min-width: 155px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  overflow: hidden;
}
.tdp-alert-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 12px 12px 0 0;
}
.tdp-alert-card--red::after {
  background: #dc2626;
}
.tdp-alert-card--orange::after {
  background: #ea580c;
}
.tdp-alert-card--amber::after {
  background: #d97706;
}
.tdp-alert-card--blue::after {
  background: #2563eb;
}
.tdp-alert-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tdp-alert-val {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1e293b;
  font-family: "IBM Plex Mono", monospace;
  line-height: 1.1;
}
.tdp-alert-lbl {
  font-size: 0.7rem;
  font-weight: 700;
  color: #374151;
}
.tdp-alert-sub {
  font-size: 0.6rem;
  color: #9ca3af;
}

/* ── PANELS ── */
.tdp-panel {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  min-width: 0;
}
.tdp-panel-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 9px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #374151;
}

/* ── TABS ── */
.tdp-tab-grp {
  display: flex;
  gap: 2px;
  background: #f1f5f9;
  border-radius: 5px;
  padding: 2px;
}
.tdp-tab {
  padding: 3px 9px;
  font-size: 0.65rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-radius: 3px;
  font-family: "Sarabun", sans-serif;
}
.tdp-tab.active {
  background: #2563eb;
  color: #fff;
}

/* ── MAIN ROW ── */
.tdp-main-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* ── INSIGHT BAR ── */
.tdp-insight-bar {
  display: flex;
  align-items: center;
  padding: 5px 12px 9px;
  font-size: 0.7rem;
  color: #4b5563;
  border-top: 1px solid #f1f5f9;
}

/* ── DECLINING PROVINCES ── */
.tdp-dec-list {
  padding: 6px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tdp-dec-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px;
  border-radius: 5px;
}
.tdp-dec-row:hover {
  background: #fef2f2;
}
.tdp-dec-rank {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f1f5f9;
  color: #6b7280;
}
.tdp-dec-rank--hot {
  background: #fef2f2;
  color: #dc2626;
}
.tdp-dec-info {
  flex: 1;
  min-width: 0;
}
.tdp-dec-name {
  font-size: 0.74rem;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tdp-dec-region {
  font-size: 0.6rem;
  color: #9ca3af;
}
.tdp-dec-nums {
  text-align: right;
  flex-shrink: 0;
}
.tdp-dec-change {
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}
.tdp-dec-change--neg {
  color: #dc2626;
}
.tdp-dec-pct--neg {
  font-size: 0.62rem;
  color: #dc2626;
}
.tdp-dec-sparkbar {
  width: 50px;
  flex-shrink: 0;
}
.tdp-dec-bar-bg {
  height: 4px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}
.tdp-dec-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

/* ── RISK TABLE ── */
.tdp-risk-tbl-wrap {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}
.tdp-risk-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.76rem;
}
.tdp-risk-tbl thead {
  position: sticky;
  top: 0;
  z-index: 2;
}
.tdp-risk-tbl thead tr {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}
.tdp-risk-tbl th {
  padding: 7px 9px;
  font-size: 0.61rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.tdp-risk-tbl td {
  padding: 6px 9px;
  border-bottom: 1px solid #f1f5f9;
  color: #374151;
}
.tdp-risk-row:hover td {
  background: #fafafa;
}
.tdp-risk-name {
  font-weight: 600;
  color: #1e293b;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tdp-risk-prov {
  font-size: 0.74rem;
  font-weight: 600;
  color: #1e293b;
}
.tdp-risk-region {
  font-size: 0.6rem;
  color: #9ca3af;
}

.tdp-search {
  max-width: 155px !important;
}
.tdp-search >>> .v-input__slot {
  min-height: 30px !important;
  background: #f8fafc !important;
}
.tdp-search >>> input {
  font-size: 0.74rem !important;
  font-family: "Sarabun", sans-serif !important;
}

.tdp-load-more {
  text-align: center;
  padding: 8px;
  font-size: 0.73rem;
  color: #2563eb;
  cursor: pointer;
  background: #f0f9ff;
  border-top: 1px solid #dbeafe;
}
.tdp-load-more:hover {
  background: #dbeafe;
}
.tdp-no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.78rem;
}

/* ── RISK BADGES ── */
.risk-badge {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.63rem;
  font-weight: 700;
}
.risk-badge--critical {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}
.risk-badge--high {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}
.risk-badge--low {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

/* ── UTIL ── */
.chg-neg {
  color: #dc2626;
  font-weight: 700;
}
.chg-pos {
  color: #16a34a;
  font-weight: 700;
}
.ta-r {
  text-align: right !important;
}
.ta-c {
  text-align: center !important;
}
.fw6 {
  font-weight: 700;
}
.mono-sm {
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.72rem;
}

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .tdp-main-row {
    flex-direction: column;
  }
  .tdp-alert-card {
    min-width: 140px;
  }
}
</style>
